import * as Immutable from 'immutable';
import { handleActions } from 'redux-actions';

import { isdeviceUpToTablet } from '../../../shared/helpers/responsive';
import { EditorActionType } from '../../EditorReducer/EditorReducer.types';

import {
  BaseFiltersStoreElement,
  FiltersActionType,
  FiltersStoreList,
  SimulationFilter,
  SimulationInfoFilters,
} from './FiltersReducer.types';
import { INFO_SIMULATION_FILTERS_MAP } from './FiltersConfig';

const initialState: FiltersStoreList = Immutable.List();

const filterTabPriorities: Record<SimulationFilter, number> = {
  code: 20,
  superCode: 20,
  results: 15,
  tutorial: 13,
  learn: 10,
  dataExplorer: 5,
};
const ACTIVATED_FILTER_PRIORITY = Infinity;

export const resolveFilterConflicts = (
  storeFilter: BaseFiltersStoreElement = {
    code: false,
    results: false,
    learn: false,
    superCode: false,
    dataExplorer: false,
    tutorial: false,
  },
  filterKey: SimulationFilter
) => {
  const sortedStoreFilterEntries = (Object.entries(storeFilter) as [SimulationFilter, boolean][]).sort(([key1], [key2]) => {
    const firstFilterPriority = key1 === filterKey ? ACTIVATED_FILTER_PRIORITY : filterTabPriorities[key1];
    const secondFilterPriority = key2 === filterKey ? ACTIVATED_FILTER_PRIORITY : filterTabPriorities[key2];
    return firstFilterPriority - secondFilterPriority;
  });
  const activeFiltersEntries = sortedStoreFilterEntries.filter(([key, value]) => value);
  const activeFiltersCount = activeFiltersEntries.length;

  let result = { ...storeFilter };
  if (activeFiltersCount === 0) {
    return { ...result, [filterKey]: true };
  }

  const leastImportantFilter = activeFiltersEntries[0][0];
  if (activeFiltersCount > 2) {
    result[leastImportantFilter] = false;
  }

  const firstActiveFilter = activeFiltersEntries[activeFiltersEntries.length - 1]?.[0];
  const secondActiveFilter = activeFiltersEntries[activeFiltersEntries.length - 2]?.[0];

  if (
    firstActiveFilter &&
    secondActiveFilter &&
    INFO_SIMULATION_FILTERS_MAP.has(firstActiveFilter as SimulationInfoFilters) &&
    INFO_SIMULATION_FILTERS_MAP.has(secondActiveFilter as SimulationInfoFilters)
  ) {
    return { ...result, [secondActiveFilter]: false };
  }
  return result;
};

const FiltersReducer = handleActions<any>(
  {
    TOGGLE_SIMULATION_FILTER: (state: FiltersStoreList, action: FiltersActionType) => {
      const filter = action.payload.filter;
      const index = action.payload.tabIndex;
      const filterElement = state.get(index);
      if (filterElement) {
        const newState = state.setIn([index, filter], !state.getIn([index, filter])).toJS()[index] as BaseFiltersStoreElement | undefined;

        const newStateWithoutConflicts = resolveFilterConflicts(newState, filter);

        return state.setIn([index], Immutable.Map(Immutable.fromJS(newStateWithoutConflicts)));
      } else {
        return state;
      }
    },
    TOGGLE_SIMULATION_FILTER_MOBILE: (state: FiltersStoreList, action: FiltersActionType) => {
      const index = action.payload.tabIndex;
      const codeType = action.payload.codeType ? action.payload.codeType : 'code';
      const result = state.getIn([index, 'results']);
      const code = state.getIn([index, codeType]);

      return state.setIn([index, 'results'], !result).setIn([index, codeType], !code);
    },
    CLONE_ALPHA: (state: FiltersStoreList, action: FiltersActionType) => {
      const codeType = action.payload.codeType ? action.payload.codeType : 'code';
      if (action.payload.indexChild === null) {
        return state.push(
          // @ts-ignore
          Immutable.fromJS({
            [codeType]: true,
            dataExplorer: false,
            tutorial: false,
            results: isdeviceUpToTablet() ? false : true,
            learn: false,
          })
        );
      } else {
        return state;
      }
    },
    CLONE_ALPHA_FROM_CONTENT: (state: FiltersStoreList, action: FiltersActionType) => {
      const data = action.payload.data ? Immutable.fromJS(action.payload.data) : null;
      if (data && data.get('type') === 'SUPER') {
        return state.push(
          // @ts-ignore
          Immutable.fromJS({
            dataExplorer: false,
            superCode: true,
            results: isdeviceUpToTablet() ? false : true,
            learn: false,
          })
        );
      } else {
        return state.push(
          // @ts-ignore
          Immutable.fromJS({
            dataExplorer: false,
            tutorial: false,
            code: true,
            results: isdeviceUpToTablet() ? false : true,
            learn: false,
          })
        );
      }
    },

    SET_DEFAULT_FILTERS: (state: FiltersStoreList, action: FiltersActionType) => {
      const filters = action.payload.filter;
      const index = action.payload.tabIndex;

      // @ts-ignore
      return state.set(index, Immutable.fromJS(filters));
    },

    CREATE_NEW_TAB: (state: FiltersStoreList, action: EditorActionType) => {
      const filter = {
        code: true,
        results: isdeviceUpToTablet() ? false : true,
        learn: false,
        dataExplorer: false,
        tutorial: false,
      };

      // @ts-ignore
      return state.push(Immutable.fromJS(filter));
    },

    CREATE_NEW_SUPER_ALPHA_TAB: (state: FiltersStoreList) =>
      state.push(
        // @ts-ignore
        Immutable.fromJS({
          dataExplorer: false,
          superCode: true,
          results: isdeviceUpToTablet() ? false : true,
          learn: false,
          tutorial: false,
        })
      ),
    CREATE_NEW_MULTI_TAB: (state: FiltersStoreList) =>
      state.push(
        // @ts-ignore
        Immutable.fromJS({
          dataExplorer: false,
          code: true,
          results: isdeviceUpToTablet() ? false : true,
          learn: false,
          tutorial: false,
        })
      ),
    CHANGE_TAB_POSITION: (state: FiltersStoreList, action: FiltersActionType) => {
      const tabToChange = state.get(action.payload.source);
      if (tabToChange) {
        return state.delete(action.payload.source).insert(action.payload.destination, tabToChange);
      } else {
        return state;
      }
    },
    UPDATE_FILTERS_FOR_SINGLE_FILTER: (state: FiltersStoreList) =>
      state.map((tab: any) =>
        tab
          .set('results', false)
          .set('learn', false)
          .set('dataExplorer', false)
          .set('tutorial', false)
          .set(tab.get('code') === undefined ? 'superCode' : 'code', true)
      ),
    CLOSE_TAB: (state: FiltersStoreList, action: FiltersActionType) => state.remove(action.payload.tabIndex),
    AUTHENTICATION_LOGOUT: () => initialState,
    RESET_FILTERS: () => initialState,
  },
  initialState
);

export default FiltersReducer;
